import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";

/**
 * Saisie d'une date de naissance pensée pour le mobile.
 *
 * Le natif <input type="date"> ouvre un calendrier positionné sur le mois
 * courant : pour atteindre 2015 le parent doit faire défiler des dizaines de
 * mois, ce qui bloque beaucoup d'entre eux. On remplace ce calendrier par
 * trois listes déroulantes (jour / mois / année) : sur mobile chacune ouvre la
 * roulette native du système, en deux ou trois gestes la date est saisie.
 *
 * La valeur reste au format ISO "YYYY-MM-DD" (ou "") pour ne rien changer
 * côté API.
 */

const MOIS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

const pad = (n) => String(n).padStart(2, "0");

const joursDansLeMois = (annee, mois) => {
  if (!mois) return 31;
  if (!annee) return mois === 2 ? 29 : new Date(2024, mois, 0).getDate();
  return new Date(Number(annee), Number(mois), 0).getDate();
};

const parseValue = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!match) return { annee: "", mois: "", jour: "" };
  return { annee: match[1], mois: match[2], jour: match[3] };
};

const calculerAge = (annee, mois, jour) => {
  if (!annee || !mois || !jour) return null;
  const naissance = new Date(Number(annee), Number(mois) - 1, Number(jour));
  const aujourdhui = new Date();
  let age = aujourdhui.getFullYear() - naissance.getFullYear();
  const moisEcoules = aujourdhui.getMonth() - naissance.getMonth();
  if (moisEcoules < 0 || (moisEcoules === 0 && aujourdhui.getDate() < naissance.getDate())) {
    age -= 1;
  }
  return age;
};

const selectClass =
  "w-full appearance-none bg-white text-[#000] text-base font-medium " +
  "border-2 border-gray rounded-xl py-3 pl-3 pr-8 focus:outline-none " +
  "focus:border-secondary focus:ring-2 focus:ring-secondary/20 transition-colors";

function Chevron() {
  return (
    <svg
      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[rgba(0,0,0,0.45)]"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function DateNaissanceInput({
  value,
  onChange,
  id = "date_naissance",
  label = "Date de naissance",
  // Plage large volontairement : du tout-petit qui entre en préscolaire à
  // l'élève le plus âgé du lycée, avec de la marge de part et d'autre.
  // Une année en dehors reste sélectionnable si elle est déjà enregistrée.
  ageMin = 0,
  ageMax = 30,
  error,
}) {
  // La valeur remontée ne devient une date ISO que lorsque les trois listes
  // sont renseignées : on garde donc la saisie en cours en interne, sinon le
  // premier choix du parent serait effacé par le re-rendu suivant.
  const [parts, setParts] = useState(() => parseValue(value));
  const { annee, mois, jour } = parts;

  useEffect(() => {
    if (!value) return; // saisie partielle en cours : on ne touche à rien
    const recue = parseValue(value);
    setParts((actuelles) =>
      recue.annee === actuelles.annee &&
      recue.mois === actuelles.mois &&
      recue.jour === actuelles.jour
        ? actuelles
        : recue
    );
  }, [value]);

  const anneeCourante = new Date().getFullYear();
  const annees = useMemo(() => {
    const liste = [];
    for (let a = anneeCourante - ageMin; a >= anneeCourante - ageMax; a -= 1) {
      liste.push(a);
    }
    // Une date déjà enregistrée hors de la plage doit rester sélectionnable,
    // sinon le champ apparaîtrait vide en modification.
    if (annee && !liste.includes(Number(annee))) {
      liste.push(Number(annee));
      liste.sort((a, b) => b - a);
    }
    return liste;
  }, [anneeCourante, ageMin, ageMax, annee]);

  const nbJours = joursDansLeMois(annee, mois);
  const jours = useMemo(
    () => Array.from({ length: nbJours }, (_, i) => i + 1),
    [nbJours]
  );

  const age = calculerAge(annee, mois, jour);

  const emettre = (next) => {
    // Le 31 d'un mois qui n'en compte que 30 est ramené au dernier jour valide.
    const maxJour = joursDansLeMois(next.annee, next.mois);
    const jourCorrige =
      next.jour && Number(next.jour) > maxJour ? pad(maxJour) : next.jour;

    const majour = { ...next, jour: jourCorrige };
    setParts(majour);

    onChange(
      majour.annee && majour.mois && majour.jour
        ? `${majour.annee}-${majour.mois}-${majour.jour}`
        : ""
    );
  };

  return (
    <div>
      <label
        htmlFor={`${id}-jour`}
        className="block text-sm text-[rgba(0,0,0,0.6)] font-bold mb-2"
      >
        {label}
      </label>

      <div className="grid grid-cols-[1fr_1.6fr_1.2fr] gap-2">
        <div className="relative">
          <select
            id={`${id}-jour`}
            aria-label="Jour de naissance"
            className={selectClass}
            value={jour}
            onChange={(e) => emettre({ annee, mois, jour: e.target.value })}
          >
            <option value="">Jour</option>
            {jours.map((j) => (
              <option key={j} value={pad(j)}>
                {j}
              </option>
            ))}
          </select>
          <Chevron />
        </div>

        <div className="relative">
          <select
            id={`${id}-mois`}
            aria-label="Mois de naissance"
            className={selectClass}
            value={mois}
            onChange={(e) => emettre({ annee, mois: e.target.value, jour })}
          >
            <option value="">Mois</option>
            {MOIS.map((nom, i) => (
              <option key={nom} value={pad(i + 1)}>
                {nom}
              </option>
            ))}
          </select>
          <Chevron />
        </div>

        <div className="relative">
          <select
            id={`${id}-annee`}
            aria-label="Année de naissance"
            className={selectClass}
            value={annee}
            onChange={(e) => emettre({ annee: e.target.value, mois, jour })}
          >
            <option value="">Année</option>
            {annees.map((a) => (
              <option key={a} value={String(a)}>
                {a}
              </option>
            ))}
          </select>
          <Chevron />
        </div>
      </div>

      {error ? (
        <p className="text-danger text-sm mt-2">{error}</p>
      ) : age !== null && age >= 0 ? (
        // Une date encore à venir dans l'année en cours ne donne pas d'âge
        // affichable : on retombe sur le texte d'aide.
        <p className="text-sm text-[rgba(0,0,0,0.55)] mt-2">
          {age} an{age > 1 ? "s" : ""}
        </p>
      ) : (
        <p className="text-sm text-[rgba(0,0,0,0.45)] mt-2">
          Facultatif — aide à adapter les exercices.
        </p>
      )}
    </div>
  );
}

DateNaissanceInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string,
  label: PropTypes.string,
  ageMin: PropTypes.number,
  ageMax: PropTypes.number,
  error: PropTypes.string,
};

export default DateNaissanceInput;
